import React, { useEffect, useState } from "react";
import { DataGrid, GridColDef, GridActionsCellItem } from "@mui/x-data-grid";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Add, Edit, Delete, Visibility } from "@mui/icons-material";
import zodiac_love from "./../../../data/zodiac/zodiac_love.json";
import { ENDPOINTS, getApiUrl } from "../../../common/endpoints";
import { Requests } from "../../../common/requests";
import { IS_SHOW_IMPORT, ZodiacOptions } from "../../../constants";
import { data_grid_styles, text_field_styles } from "../../../constants/style";
import { useSnackbar } from "notistack";
import Swal from "sweetalert2";
import TogglePublicBtn from "../../../components/TogglePublicBtn";
interface ZodiacData {
  id?: string;
  signA: string;
  signB: string;
  compatibility: string;
  pros: string;
  cons: string;
}

const ZodiacLoveCompatibility: React.FC = () => {
  const [data, setData] = useState<ZodiacData[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [editingRow, setEditingRow] = useState<ZodiacData | null>(null);
  const [selectedRow, setSelectedRow] = useState<ZodiacData | null>(null);
  const [formData, setFormData] = useState<ZodiacData>({
    signA: "",
    signB: "",
    compatibility: "",
    pros: "",
    cons: "",
  });
  const [isImporting, setIsImporting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    getData();
  }, []);
  const getData = async () => {
    const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_LOVE_LIST + "?limit=0");
    try {
      const data = await Requests.get(apiUrl);
      if (Array.isArray(data)) setData(data);
    } catch (e) {}
  };
  // DataGrid columns
  const columns: GridColDef[] = [
    { field: "signA", headerName: "Sign A", width: 100 },
    { field: "signB", headerName: "Sign B", width: 130 },
    {
      field: "compatibility",
      headerName: "Compatibility",
      width: 300,
      flex: 1,
    },
    {
      field: "pros",
      headerName: "Pros",
      width: 300,
      flex: 1,
    },
    {
      field: "cons",
      headerName: "Cons",
      width: 300,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 270,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Edit />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="Delete"
          onClick={() => handleDelete(params.id.toString())}
        />,
        <GridActionsCellItem
          icon={<Visibility />}
          label="View"
          onClick={() => handleView(params.row)}
        />,
        <TogglePublicBtn
          isValidated={!!params.row?.ispublished}
          onToggle={() => {
            onTogglePublish(params.row?.id);
          }}
        />,
      ],
    },
  ];

  const onTogglePublish = async (id: string) => {
    try {
      const apiUrl = getApiUrl(
        ENDPOINTS.ZODIAC_LOVE_TOGGLE_PUBLISH.replace(":id", id)
      );
      await Requests.patch(apiUrl);
      getData();
      enqueueSnackbar("Toggle publish success!", { variant: "success" });
    } catch (e) {}
  };

  // Add new row
  const handleAdd = () => {
    setOpenDialog(true);
    setEditingRow(null);
    setFormData({
      signA: "",
      signB: "",
      compatibility: "",
      pros: "",
      cons: "",
    });
  };

  // Edit existing row
  const handleEdit = (row: ZodiacData) => {
    setEditingRow(row);
    setFormData(row);
    setOpenDialog(true);
  };

  // View details
  const handleView = (row: ZodiacData) => {
    setSelectedRow(row);
    setDetailsDialog(true);
  };

  // Save form data
  const handleSave = async () => {
    try {
      if (editingRow?.id) {
        setData(
          data.map((item) => (item.id === editingRow.id ? formData : item))
        );
        const apiUrl = getApiUrl(
          ENDPOINTS.ZODIAC_LOVE_EDIT.replace(":id", editingRow.id)
        );
        await Requests.put(apiUrl, formData);
      } else {
        const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_LOVE_ADD);
        await Requests.post(apiUrl, formData);
      }
      setOpenDialog(false);
      setFormData({
        signA: "",
        signB: "",
        compatibility: "",
        pros: "",
        cons: "",
      });
      getData();
      enqueueSnackbar("Save successfully!", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Save unsuccessfully!", { variant: "error" });
    }
  };

  // Delete row
  const handleDelete = async (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const apiUrl = getApiUrl(
            ENDPOINTS.ZODIAC_LOVE_DELETE.replace(":id", id)
          );
          await Requests.delete(apiUrl);
          getData();
          Swal.fire("Deleted!", "Delete sucessfully.", "success");
        } catch (e) {
          Swal.fire("Deleted!", "Delete unsucessfully.", "error");
        }
      }
    });
  };

  // Handle input changes
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  // Dialog close
  const handleClose = () => {
    setOpenDialog(false);
  };

  // Detail dialog close
  const handleDetailsClose = () => {
    setDetailsDialog(false);
    setSelectedRow(null);
  };

  const onImportData = async () => {
    setIsImporting(true);
    const apiUrl = getApiUrl(ENDPOINTS.ZODIAC_LOVE_BULK);
    try {
      const nb_pages = 10;
      const nbReq = zodiac_love.length / nb_pages;
      for (let i = 0; i <= nbReq; i++) {
        await Requests.post(
          apiUrl,
          zodiac_love.slice(i * 10, (i + 1) * nb_pages).map((item) => ({
            signA: item.signA,
            signB: item.signB,
            compatibility: item.compatibility,
            pros: item.pros,
            cons: item.cons,
          }))
        );
      }
      getData();
      enqueueSnackbar("Import successfully!", { variant: "success" });
    } catch (e) {
      enqueueSnackbar("Import unsuccessfully!", { variant: "error" });
    }
    setIsImporting(false);
  };

  return (
    <Box m={2}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          color="primary"
          variant="h5"
          sx={{
            fontWeight: "bold",
          }}
          gutterBottom
        >
          Zodiac Love
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            sx={{
              textTransform: "unset",
              marginRight: "10px",
            }}
            startIcon={<Add />}
            onClick={handleAdd}
          >
            Add
          </Button>
          {IS_SHOW_IMPORT && (
            <Button
              variant="contained"
              color="primary"
              sx={{
                textTransform: "unset",
              }}
              onClick={() => {
                onImportData();
              }}
              disabled={isImporting}
            >
              {isImporting && (
                <CircularProgress
                  size="30px"
                  sx={{
                    marginRight: "10px",
                    color: "#fff",
                  }}
                />
              )}
              Import
            </Button>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          height: "calc(100vh - 80px)",
          overflowY: "auto",
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          checkboxSelection={false}
          rowHeight={100}
          sx={{
            ...data_grid_styles,
          }}
        />
      </Box>

      <Dialog open={openDialog} onClose={handleClose}>
        <DialogTitle
          color="primary"
          sx={{
            fontWeight: "bold",
          }}
        >
          {editingRow ? "Edit Zodiac" : "Add Zodiac"}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            name="signA"
            label="Sign A"
            fullWidth
            value={formData.signA}
            onChange={handleChange}
            required
            sx={{
              ...text_field_styles,
            }}
          >
            {ZodiacOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            name="signB"
            label="Sign B"
            fullWidth
            value={formData.signB}
            onChange={handleChange}
            required
            sx={{
              ...text_field_styles,
            }}
          >
            {ZodiacOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            name="compatibility"
            label="Compatibility"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.compatibility}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="pros"
            label="Pros"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.pros}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
          <TextField
            margin="dense"
            name="cons"
            label="Cons"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.cons}
            onChange={handleChange}
            sx={{
              ...text_field_styles,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="secondary"
            sx={{ textTransform: "unset" }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: "unset" }}
            type="submit"
            onClick={() => handleSave()}
          >
            {editingRow ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      {selectedRow && (
        <Dialog open={detailsDialog} onClose={handleDetailsClose}>
          <DialogTitle>Zodiac Love Details</DialogTitle>
          <DialogContent>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Sign A</strong>: {selectedRow.signA}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Sign B</strong>: {selectedRow.signB}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Compatibility</strong>: {selectedRow.compatibility}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Pros</strong>: {selectedRow.pros}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
              }}
            >
              <strong>Cons</strong>: {selectedRow.cons}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                textTransform: "unset",
              }}
              onClick={handleDetailsClose}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ZodiacLoveCompatibility;
